import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import {
  trackInitialValues,
  TrackTabs,
} from 'components/organisms/forms/track/TrackForm';
import { useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { PiPencilSimpleLine, PiStorefront } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useDeleteTrack, useGetTrack } from 'services/tracks';
import { STATUS } from 'services/types';
import { getFinanceStatisticsBackPath } from 'utils/nav/financeStatisticsNavigation.utils';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { usePostMusicProductDigitalFromTrack } from 'services/music-product-digital';
import { onErrorShowToast } from 'utils/service/errorHandler';

const Track = () => {
  const trackId = useParamId('track');
  const { pathname, state: locationState } = useLocation();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  const removeTrack = useDeleteTrack(trackId);
  const { data: track, isLoading } = useGetTrack(trackId);
  const createSingleFromTrack = usePostMusicProductDigitalFromTrack();

  const onRemoveTrack = async (actions: PopupActions) => {
    await removeTrack?.mutateAsync(undefined, {
      onError: error => {
        actions.close();
        onErrorShowToast(error);
      },
      onSuccess: () => {
        navigate('/track');
      },
    });
  };

  const onCreateSingle = async () => {
    if (!trackId) return;
    await createSingleFromTrack.mutateAsync(
      { trackId },
      {
        onError: error => {
          onErrorShowToast(error);
        },
        onSuccess: productId => {
          if (productId) {
            navigate(`/music-product-digital/${productId}/contracts`);
          }
        },
      }
    );
  };

  return (
    <Form.Track
      i18nextPrefix='track'
      initialValues={
        track ? middleware.track.queryToForm(track) : trackInitialValues
      }
      disableTabs={
        track?.trackStatus === STATUS.draft
          ? [TrackTabs.statistics, TrackTabs.fingerprints]
          : []
      }
      onSubmit={() => {}}
      tittle={getMainTitle(track?.mainTitle)}
      backPath={getFinanceStatisticsBackPath('track', locationState)}
      status={{ isLoading: isLoading, displayMode: true }}
      topbarContent={
        <div className='flex w-full justify-end items-center gap-2'>
          {track?.trackStatus !== STATUS.draft && (
            <Button
              isClear
              disabled={!trackId || createSingleFromTrack.isPending}
              onClick={onCreateSingle}>
              <Button.Icon icon={<PiStorefront />} />
              <Button.Text text={t('track.create-single')} />
            </Button>
          )}
          {hasPermission('TrackPermissionsLIST**', 2) && (
            <Button
              isClear
              onlyIcon
              onClick={() => {
                track &&
                  navigate(
                    `/track/edit/${trackId}/${getLastPathname(pathname)}`,
                    {
                      replace: true,
                    }
                  );
              }}>
              <Button.Description description={t('button.edit')} />

              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('TrackPermissionsLIST**', 4) && (
            <MagellanPopups.DeleteWarning
              name={track?.mainTitle.title}
              onRemove={onRemoveTrack}
            />
          )}
        </div>
      }
    />
  );
};

export default Track;
